/**
 * Courier Performance — shared utilities, color coding, delivery types, dummy data.
 * Ported from Admin Panel's courierPerformance module.
 */
import jwt_decode from 'jwt-decode'
import {SUBTLE} from '../../../styles/foundation/colors'
import {
  delivered as DELIVERED_STATUSES, rto as RTO_STATUSES, placed as PLACED_STATUSES,
  flattenResponseData, getShipmentInfo, getYYYYMMDD,
} from '../../../helpers/UtilityHelper'
import {apiConstants, getGeneric, postGeneric} from '../../../common/constants'
import {getMerchant, getToken} from '../../../helpers/ReduxHelpers'

// Merchant ObjectId for API calls — prefer the selected merchant, else the JWT's merchantOid
// (getMerchant() can be empty depending on session, so the token is the reliable fallback).
export function resolveMerchantId() {
  const id = getMerchant()?.id
  if (id) return id
  try {
    const token = getToken()
    return token ? jwt_decode(token)?.merchantOid : undefined
  } catch (e) {
    return undefined
  }
}

// Reverse shipments not yet (or never) picked up — mirrors rvp/useGetData.js.
const PENDING_PICKUP_STATUSES = ['CANCELLED_ORDER', 'AWB_REGISTERED', 'PICKUP_PENDING', 'PICKUP_FAILED']

// Calendar-day difference (ignoring time of day), e.g. registered → actual pickup.
const dayDiff = (later, earlier) => {
  if (!later || !earlier) return null
  const a = new Date(later); a.setHours(0, 0, 0, 0)
  const b = new Date(earlier); b.setHours(0, 0, 0, 0)
  return Math.round((a.getTime() - b.getTime()) / 86400000)
}

// ── Performance color thresholds (from Admin Panel PerformanceStyled.js) ──
const THRESHOLDS = {
  default: {green: 85, amber: 75},
  tat: {green: 95, amber: 85},
  tatPlusOne: {green: 97, amber: 90},
  tatPlusTwo: {green: 98, amber: 92},
  tatPlusThree: {green: 99, amber: 95},
  prepaid: {green: 98, amber: 90},
  cod: {green: 80, amber: 70},
}

export const PERF_COLORS = {green: '#10b981', amber: '#f59e0b', red: '#ef4444'}

export function getPerformanceColor(value, metricType = 'default') {
  if (value == null || isNaN(value)) return SUBTLE
  const t = THRESHOLDS[metricType] || THRESHOLDS.default
  if (value >= t.green) return PERF_COLORS.green
  if (value >= t.amber) return PERF_COLORS.amber
  return PERF_COLORS.red
}

// ── Delivery type classifications ──
// Forward service-type buckets. Values match the buckets returned by
// bucketForServiceType() so the dropdown filters directly.
export const FORWARD_SUB_TYPES = [
  {label: 'All Forward', value: 'ALL'},
  {label: 'Surface', value: 'SURFACE'},
  {label: 'Air', value: 'AIR'},
  {label: 'Same Day Delivery (SDD)', value: 'SDD'},
  {label: 'Same City NDD', value: 'SAME_CITY_NDD'},
  {label: 'Inter-City NDD', value: 'INTERCITY_NDD'},
]

export const REVERSE_SUB_TYPES = [
  {label: 'All Reverse', value: 'ALL'},
]

// Service-type options for the Courier Comparison "within same service type" dropdown.
export const SERVICE_TYPE_OPTIONS = [
  {label: 'Surface', value: 'SURFACE'},
  {label: 'Air', value: 'AIR'},
  {label: 'SDD', value: 'SDD'},
  {label: 'Same City NDD', value: 'SAME_CITY_NDD'},
  {label: 'Inter-City NDD', value: 'INTERCITY_NDD'},
]

// D2C Courier Performance route segment → default service-type bucket.
export const SECTION_TO_BUCKET = {
  courier: 'SURFACE', air: 'AIR', sdd: 'SDD', 'sc-ndd': 'SAME_CITY_NDD', 'ic-ndd': 'INTERCITY_NDD',
}

// Map a service_type_master code (e.g. "FWD_SURFACE_REG", "SDD_FWD_REG",
// "Intercity_NDD_REG", "REV_REG_QC") → a performance bucket. Falls back to the
// account's dispatchMode (SURFACE/AIR) when no code is present.
export function bucketForServiceType(code, dispatchMode) {
  const c = String(code || '').toUpperCase()
  if (c) {
    if (c.includes('SDD')) return 'SDD'
    // Same-City NDD is coded SC-NDD_* / SC_NDD_* (e.g. SC-NDD_FWD_REG) — check
    // before the generic NDD fallback so it isn't folded into Inter-City.
    if (c.includes('SC-NDD') || c.includes('SC_NDD') || c.includes('SAME')) return 'SAME_CITY_NDD'
    if (c.includes('INTERCITY') || c.includes('INTER_CITY') || c.includes('IC_NDD') || c.includes('IC-NDD')) return 'INTERCITY_NDD'
    if (c.includes('NDD')) return 'INTERCITY_NDD'
    if (c.startsWith('REV') || c.includes('REVERSE')) return 'REVERSE'
    if (c.includes('AIR')) return 'AIR'
    if (c.includes('SURFACE')) return 'SURFACE'
  }
  if (dispatchMode === 'AIR') return 'AIR'
  if (dispatchMode === 'SURFACE') return 'SURFACE'
  return 'OTHER'
}

// Resolve a metrics courierId (= courier id or cpAccountCode) to its parent
// courier name and service-type bucket.
function classifyCourier(couriers, cId) {
  const c = couriers?.find((x) => x.id === cId || x.cpAccountCode === cId)
  return {
    parent: c?.parent || c?.name || cId,
    bucket: bucketForServiceType(c?.serviceTypePrimary, c?.dispatchMode),
  }
}

// True when a row's bucket passes the requested filter.
// filter: null = no filter · 'ALL' / 'FORWARD' = any non-reverse · else exact match.
function passesBucket(filter, bucket) {
  if (!filter) return true
  if (filter === 'ALL' || filter === 'FORWARD') return bucket !== 'REVERSE'
  return bucket === filter
}

// ── Comparison metrics ──
export const COMPARISON_METRICS = [
  {label: 'Overall Delivery %', value: 'deliveryPct', group: 'delivery'},
  {label: 'First Attempt Strike Rate %', value: 'firstAttemptPct', group: 'delivery'},
  {label: 'COD Delivery %', value: 'codDeliveryPct', group: 'delivery'},
  {label: 'Prepaid Delivery %', value: 'prepaidDeliveryPct', group: 'delivery'},
  {label: 'RTO %', value: 'rtoPct', group: 'delivery'},
  {label: 'TAT Adherence %', value: 'tatPct', group: 'tat'},
  {label: 'TAT+1 Adherence %', value: 'tatPlusOnePct', group: 'tat'},
  {label: 'TAT+2 Adherence %', value: 'tatPlusTwoPct', group: 'tat'},
  {label: 'RVP Pickup %', value: 'rvpPickupPct', group: 'rvp'},
  {label: 'RVP+1 Adherence %', value: 'rvpPlusOnePct', group: 'rvp'},
]

// ── Data processing ──
const pct = (num, den) => den > 0 ? Math.round((num / den) * 10000) / 100 : 0

// ── Merchant-scoped raw fetchers ──
// Both Courier Performance (Forward/Reverse tabs) and Courier Comparison use these so
// every number is THIS merchant's own data, not the global CT benchmark.
// ALL_AWB_REGISTERED_BY_DATE_RANGE is a GET endpoint (the analytics hooks call it with no
// `type` → baseQuery routes to GET); the detail calls are POST.

// Forward: registered FORWARD shipments, delivered ones merged with delivery details
// (firstAttemptWithinSLA / isBreached / deliveryBreachedAgeingDays for TAT columns).
export async function fetchForwardRaw(range) {
  const from = getYYYYMMDD(range[0].startDate)
  const to = getYYYYMMDD(range[0].endDate)
  const merchant = getMerchant()
  const merchantId = merchant?.id
  const shipmentInfo = getShipmentInfo(merchant, 'B2C')

  const regRes = await getGeneric(apiConstants.ALL_AWB_REGISTERED_BY_DATE_RANGE, {
    from_date: from, to_date: to, merchant_list: merchantId, orderType_list: 'FORWARD', shipmentType_list: shipmentInfo,
  })
  const registered = flattenResponseData(regRes?.data || [])
  const deliveredAwbs = registered.filter((r) => DELIVERED_STATUSES.includes(r.orderStatus)).map((r) => r.awbNumber)

  const detailMap = new Map()
  if (deliveredAwbs.length) {
    const detRes = await postGeneric(apiConstants.get_specific_data_delivery, [{merchantId, awbNumbers: deliveredAwbs}])
    ;(detRes?.data || []).forEach((d) => detailMap.set(d.awbNumber, d))
  }
  return registered.map((r) => detailMap.has(r.awbNumber) ? {...r, ...detailMap.get(r.awbNumber)} : r)
}

// Reverse: registered REVERSE shipments, picked-up ones merged with pickup details
// (actualPickUpTime for the pickup-SLA tiers). Mirrors rvp/useGetData.js.
export async function fetchReverseRaw(range) {
  const from = getYYYYMMDD(range[0].startDate)
  const to = getYYYYMMDD(range[0].endDate)
  const merchant = getMerchant()
  const merchantId = merchant?.id
  const shipmentInfo = getShipmentInfo(merchant, 'B2C')

  const regRes = await getGeneric(apiConstants.ALL_AWB_REGISTERED_BY_DATE_RANGE, {
    from_date: from, to_date: to, merchant_list: merchantId, orderType_list: 'REVERSE', shipmentType_list: shipmentInfo,
  })
  const registered = flattenResponseData(regRes?.data || [])
  const pickedUpAwbs = registered.filter((r) => !PENDING_PICKUP_STATUSES.includes(r.orderStatus)).map((r) => r.awbNumber)

  const pickupMap = new Map()
  if (pickedUpAwbs.length) {
    const pkRes = await postGeneric(apiConstants.get_specific_data_pickup, [{merchantId, awbNumbers: pickedUpAwbs}])
    ;(pkRes?.data || []).forEach((d) => pickupMap.set(d.awbNumber, d))
  }
  return registered.map((r) => pickupMap.has(r.awbNumber) ? {...r, ...pickupMap.get(r.awbNumber)} : r)
}

// Merchant-scoped forward rows, computed from RAW order data (ALL_AWB_REGISTERED_BY_DATE_RANGE
// scoped by merchant_list, merged with get_specific_data_delivery for firstAttemptWithinSLA / isBreached).
// Replaces the global getDeliveryMetrics path so numbers are this merchant's own.
// `rows` = flattened forward registered shipments, delivered ones merged with delivery details.
export function computeForwardRowsFromRaw(rows, couriers, bucketFilter = null) {
  if (!Array.isArray(rows) || !rows.length) return []
  const grouped = {}
  for (const r of rows) {
    const cId = r.courier || r.courierId
    const {parent, bucket} = classifyCourier(couriers, cId)
    if (!passesBucket(bucketFilter, bucket)) continue
    if (!grouped[parent]) grouped[parent] = []
    grouped[parent].push(r)
  }

  return Object.entries(grouped).map(([parent, items]) => {
    // Volume = picked-up (exclude cancelled + pre-pickup "placed" statuses), mirroring useForwardDeliveries.
    const pickedUp = items.filter((i) => !['CANCELLED_ORDER', ...PLACED_STATUSES].includes(i.orderStatus))
    const volume = pickedUp.length
    const deliveredItems = items.filter((i) => DELIVERED_STATUSES.includes(i.orderStatus))
    const deliveredCount = deliveredItems.length
    const rtoCount = items.filter((i) => RTO_STATUSES.includes(i.orderStatus)).length
    // Normalise case — the snapshot can store COD/PREPAID in mixed case (matches useOverallPerformance).
    const payMode = (i) => (i.paymentMode || '').toUpperCase()
    const codPickedUp = pickedUp.filter((i) => payMode(i) === 'COD').length
    const prepaidPickedUp = pickedUp.filter((i) => payMode(i) === 'PREPAID').length
    const codDelivered = deliveredItems.filter((i) => payMode(i) === 'COD').length
    const prepaidDelivered = deliveredItems.filter((i) => payMode(i) === 'PREPAID').length
    // firstAttemptWithinSLA / isBreached / deliveryBreachedAgeingDays come from the merged
    // delivery-detail call (delivered rows only).
    const firstAttemptOk = deliveredItems.filter((i) => i.firstAttemptWithinSLA).length
    // In-TAT = not breached. TAT+1 / TAT+2 are CUMULATIVE (include in-TAT), keyed off the
    // breach-ageing days, matching forward_deliveries getTatKey (<=0 In TAT, <2 TAT+1, <3 TAT+2).
    const inTat = deliveredItems.filter((i) => !i.isBreached).length
    const inTatPlus1 = deliveredItems.filter((i) => !i.isBreached || (i.deliveryBreachedAgeingDays || 0) <= 1).length
    const inTatPlus2 = deliveredItems.filter((i) => !i.isBreached || (i.deliveryBreachedAgeingDays || 0) <= 2).length

    return {
      courierName: parent,
      volume,
      deliveryPct: pct(deliveredCount, volume),
      firstAttemptPct: pct(firstAttemptOk, deliveredCount),
      codDeliveryPct: pct(codDelivered, codPickedUp),
      prepaidDeliveryPct: pct(prepaidDelivered, prepaidPickedUp),
      rtoPct: pct(rtoCount, volume),
      tatPct: pct(inTat, deliveredCount),
      tatPlusOnePct: pct(inTatPlus1, deliveredCount),
      tatPlusTwoPct: pct(inTatPlus2, deliveredCount),
      codPct: pct(codPickedUp, volume),
      totalDelivered: deliveredCount, totalPickedup: volume,
    }
  }).sort((a, b) => b.volume - a.volume)
}

// Merchant-scoped reverse rows from RAW order data (fetchReverseRaw), grouped by PARENT courier.
// Pickup-SLA tiers are CUMULATIVE off the registered→actualPickUpTime day diff, matching the
// forward TAT pattern (Pickup % strictest, +1 / +2 progressively more lenient).
export function computeReverseRowsFromRaw(rows, couriers) {
  if (!Array.isArray(rows) || !rows.length) return []
  const grouped = {}
  for (const r of rows) {
    const cId = r.courier || r.courierId
    const {parent} = classifyCourier(couriers, cId)
    if (!grouped[parent]) grouped[parent] = []
    grouped[parent].push(r)
  }

  return Object.entries(grouped).map(([parent, items]) => {
    // RVP requests = all reverse shipments except cancelled ones.
    const volume = items.filter((i) => i.orderStatus !== 'CANCELLED_ORDER').length
    // Actually picked up (has a pickup timestamp from the merged pickup-detail call).
    const pickedUpItems = items.filter((i) => !PENDING_PICKUP_STATUSES.includes(i.orderStatus) && i.actualPickUpTime)
    const within = (days) => pickedUpItems.filter((i) => {
      const d = dayDiff(i.actualPickUpTime, i.awbRegisteredDate)
      return d != null && d <= days
    }).length

    return {
      courierName: parent,
      volume,
      rvpPickupPct: pct(within(0), volume),     // picked up same day
      rvpPlusOnePct: pct(within(1), volume),    // within +1 day (cumulative)
      rvpPlusTwoPct: pct(within(2), volume),    // within +2 days (cumulative)
      totalPickedUp: within(0),
    }
  }).sort((a, b) => b.volume - a.volume)
}

// ── Dummy data ──
export const DUMMY_FORWARD = [
  {courierName: 'Delhivery', volume: 1850, deliveryPct: 89.2, firstAttemptPct: 78.5, codDeliveryPct: 82.1, prepaidDeliveryPct: 94.8, rtoPct: 10.8, tatPct: 88.5, tatPlusOnePct: 94.2, totalDelivered: 1650, totalPickedup: 1850},
  {courierName: 'BlueDart', volume: 1200, deliveryPct: 92.1, firstAttemptPct: 85.2, codDeliveryPct: 85.5, prepaidDeliveryPct: 97.2, rtoPct: 7.9, tatPct: 95.1, tatPlusOnePct: 97.8, totalDelivered: 1105, totalPickedup: 1200},
  {courierName: 'Ecom Express', volume: 980, deliveryPct: 86.5, firstAttemptPct: 72.3, codDeliveryPct: 78.9, prepaidDeliveryPct: 92.1, rtoPct: 13.5, tatPct: 82.4, tatPlusOnePct: 90.1, totalDelivered: 848, totalPickedup: 980},
  {courierName: 'DTDC', volume: 650, deliveryPct: 84.2, firstAttemptPct: 68.7, codDeliveryPct: 76.5, prepaidDeliveryPct: 90.3, rtoPct: 15.8, tatPct: 78.9, tatPlusOnePct: 86.5, totalDelivered: 547, totalPickedup: 650},
  {courierName: 'Xpressbees', volume: 520, deliveryPct: 87.8, firstAttemptPct: 74.1, codDeliveryPct: 80.2, prepaidDeliveryPct: 93.5, rtoPct: 12.2, tatPct: 85.2, tatPlusOnePct: 92.3, totalDelivered: 457, totalPickedup: 520},
]

export const DUMMY_RVP = [
  {courierName: 'Delhivery', volume: 320, rvpPickupPct: 88.4, rvpPlusOnePct: 94.1, rvpPlusTwoPct: 97.2, totalPickedUp: 283},
  {courierName: 'Ecom Express', volume: 210, rvpPickupPct: 82.9, rvpPlusOnePct: 90.5, rvpPlusTwoPct: 95.2, totalPickedUp: 174},
  {courierName: 'Xpressbees', volume: 150, rvpPickupPct: 86.0, rvpPlusOnePct: 92.7, rvpPlusTwoPct: 96.0, totalPickedUp: 129},
  {courierName: 'BlueDart', volume: 95, rvpPickupPct: 91.6, rvpPlusOnePct: 96.8, rvpPlusTwoPct: 98.9, totalPickedUp: 87},
]
